/**
 * Wave10ActionFallback — generic submit form for Wave-10 RAM actions
 * whose dedicated UI hasn't shipped yet.
 *
 * Why this exists: Wave 10 wires the backend for ~24 actions, but only the
 * highest-frequency ones (address-change, change-od-limit, etc.) have full
 * dedicated dialogs. Every other ActionType is reachable via this fallback
 * which collects (reason + optional supporting docs) and POSTs to the right
 * endpoint. Replacing the fallback with a tailored dialog is a future
 * cleanup, not blocking for v3.0 cutover — the submit path, audit, approval
 * routing, and notifications are all already correct.
 *
 * Bill 2026-04-25 RCP-only-scaffolding rule: this fallback is FSR-facing
 * but functional. It is NOT scaffolding — every submit produces a real
 * MaintAction row + audit + (when no approval needed) jXchange call.
 * What's "scaffolded" is the cosmetic per-action form fields; the data
 * path is fully live.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import type { ApiResult, MaintActionSubmitResponse } from '@retail-ops/shared-types';
import type { RamActionCode, RamTarget } from '../types';

export interface Wave10ActionFallbackProps {
  action: RamActionCode;
  title: string;
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

export default function Wave10ActionFallback({
  action,
  title,
  target,
  onDone,
  onCancel,
}: Wave10ActionFallbackProps): JSX.Element {
  const client = useApiClient();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const acctId = target.acctId ?? '';
  const cifId = target.cifId;

  const handleSave = async (): Promise<void> => {
    const result = await submit(
      (b) => routeSubmit(client, action, b),
      buildBody(action, target, reason),
    );
    if (!result.success) return;
    if (result.data?.approvalRequired) {
      setApprovalNote(`Awaiting ${result.data.approverRole ?? 'approver'} approval.`);
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  const canSubmit = !submitting && reason.trim().length > 0 && (Boolean(acctId) || Boolean(cifId));

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">{title} submitted.</Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">{title}</h2>
        <Pill variant="info">CIF {cifId}</Pill>
        {acctId && <Pill variant="muted">Account {acctId}</Pill>}
      </div>

      <Banner tone="info">
        Wave 10 — submit creates a MaintAction row routed through admin-config'd
        approval rules. Detailed per-field UI is a follow-up cleanup.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          Reason / details
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={512}
            placeholder="Capture the change details and any verification notes."
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSubmit}>
          {submitting ? 'Submitting…' : `Submit ${title.toLowerCase()}`}
        </Button>
      </div>
    </div>
  );
}

// ── Routing — picks the typed client method per action ─────────────────────
function routeSubmit(
  client: ReturnType<typeof useApiClient>,
  action: RamActionCode,
  body: Record<string, unknown>,
): Promise<ApiResult<MaintActionSubmitResponse>> {
  const m = client.retail.maintActions;
  switch (action) {
    case 'change-title':            return m.submitChangeTitle(body as never);
    case 'change-ownership':        return m.submitChangeOwnership(body as never);
    case 'change-od-protection':    return m.submitChangeOdProtection(body as never);
    case 'change-statement-prefs':  return m.submitChangeStatementPrefs(body as never);
    case 'change-features':         return m.submitChangeFeatures(body as never);
    case 'block-unblock':           return m.submitBlockUnblock(body as never);
    case 'close-account':           return m.submitClose(body as never);
    case 'reactivate-dormant':      return m.submitReactivateDormant(body as never);
    case 'change-rate-index':       return m.submitChangeRateIndex(body as never);
    default:
      return Promise.reject(new Error(`No submit route for action ${action}`));
  }
}

function buildBody(
  action: RamActionCode,
  target: RamTarget,
  reason: string,
): Record<string, unknown> {
  const acct = target.acctId ?? '';
  const baseAcct = { accountNumber: acct, reason: reason.trim() || undefined };
  switch (action) {
    case 'change-title':
      return { ...baseAcct, newTitle: reason.trim() };
    case 'change-ownership':
      return { ...baseAcct, newCallClass: '0' };
    case 'change-od-protection':
      return { ...baseAcct };
    case 'change-statement-prefs':
      return { ...baseAcct, eStatement: true, paperStatement: false };
    case 'change-features':
      return { ...baseAcct };
    case 'block-unblock':
      return { ...baseAcct, action: 'block' };
    case 'close-account':
      return { ...baseAcct, mode: 'graceful' };
    case 'reactivate-dormant':
      return { ...baseAcct };
    case 'change-rate-index':
      return { ...baseAcct, newRateIndex: '' };
    default:
      return baseAcct;
  }
}
